import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Home, Users, Star, TrendingUp, Award, CheckCircle, Globe, Settings, Mail, Plus, Trash2, Edit, Eye, Save, ArrowUp, ArrowDown } from 'lucide-react';
import { useNotificationHook } from '../../hooks/useNotificationHook';
import { getApiBaseUrl } from '../../utils/apiUtils';

// Use the same base-URL resolution as the public site so the admin always
// reads/writes the SAME backend (localhost in dev, production otherwise).
// Previously this used REACT_APP_API_URL directly, which made the admin save
// to production while the local site read from localhost — so edits never
// appeared locally.
const API_BASE = `${getApiBaseUrl()}/services-page`;
const ICONS = [
  'CheckCircle', 'Activity', 'TrendingUp', 'Users', 'Globe', 'Settings', 'Star', 'FileText', 'MessageSquare', 'ShoppingBag', 'Eye', 'Home', 'Clock', 'Calendar', 'DollarSign', 'AlertCircle', 'Plus', 'RefreshCw', 'Palette', 'Smartphone', 'Target', 'Zap', 'Shield'
];

const SECTIONS = [
  { id: 'main', label: 'Main Section', icon: Home },
  { id: 'stats', label: 'Statistics Section', icon: TrendingUp },
  { id: 'additional', label: 'Additional Services', icon: Star },
  { id: 'process', label: 'Our Process', icon: Award },
  { id: 'testimonials', label: 'Testimonials', icon: Users },
  { id: 'cta', label: 'Call to Action', icon: CheckCircle }
];

const IconPicker = ({ value, onChange }) => {
  console.log('IconPicker value:', value); // Debug log
  const handleChange = (e) => {
    console.log('IconPicker onChange triggered:', e.target.value); // Debug log
    onChange(e.target.value);
  };
  return (
    <select 
      key={`icon-picker-${value}`} // Force re-render when value changes
      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white" 
      value={value || ''} 
      onChange={handleChange}
    >
      <option value="">Select an icon</option>
      {ICONS.map(icon => <option key={icon} value={icon}>{icon}</option>)}
    </select>
  );
};

const ServicesPageManagement = () => {
  const { notify } = useNotificationHook();
  const [activeSection, setActiveSection] = useState('main');
  const [isEditing, setIsEditing] = useState(false);
  // --- Main Section ---
  const [main, setMain] = useState({ main_title: '', gradient_title: '', description: '' });
  const [mainLoading, setMainLoading] = useState(true);
  const [mainEdit, setMainEdit] = useState(false);
  const [mainSaving, setMainSaving] = useState(false);
  const [mainError, setMainError] = useState('');
  const [mainSuccess, setMainSuccess] = useState('');

  // --- Stats Section ---
  const [stats, setStats] = useState([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statEditId, setStatEditId] = useState(null);
  const [statForm, setStatForm] = useState({ label: '', value: '', icon: ICONS[0] });
  const [statSaving, setStatSaving] = useState(false);
  const [statError, setStatError] = useState('');
  const [statSuccess, setStatSuccess] = useState('');
  const [statAddMode, setStatAddMode] = useState(false);

  // --- Additional Services ---
  const [additional, setAdditional] = useState({ additional_title: '', additional_description: '' });
  const [additionalLoading, setAdditionalLoading] = useState(true);
  const [additionalEdit, setAdditionalEdit] = useState(false);
  const [additionalSaving, setAdditionalSaving] = useState(false);
  const [additionalError, setAdditionalError] = useState('');
  const [additionalSuccess, setAdditionalSuccess] = useState('');
  const [additionalServices, setAdditionalServices] = useState([]);

  // --- Our Process ---
  const [process, setProcess] = useState({ process_title: '', process_description: '', process_steps: [] });
  const [processLoading, setProcessLoading] = useState(true);
  const [processEdit, setProcessEdit] = useState(false);
  const [processSaving, setProcessSaving] = useState(false);
  const [processError, setProcessError] = useState('');
  const [processSuccess, setProcessSuccess] = useState('');
  const [steps, setSteps] = useState([]);
  const [stepsLoading, setStepsLoading] = useState(true);
  const [stepEditId, setStepEditId] = useState(null);
  const [stepForm, setStepForm] = useState({ title: '', description: '', icon: ICONS[0] });
  const [stepSaving, setStepSaving] = useState(false);
  const [stepError, setStepError] = useState('');
  const [stepSuccess, setStepSuccess] = useState('');
  const [stepAddMode, setStepAddMode] = useState(false);
  const [forceRender, setForceRender] = useState(0); // Force re-render counter

  // --- Testimonials ---
  const [testimonials, setTestimonials] = useState([]);
  const [testimonialSection, setTestimonialSection] = useState({ testimonial_title: '', testimonial_description: '' });
  const [testimonialLoading, setTestimonialLoading] = useState(true);
  const [testimonialEdit, setTestimonialEdit] = useState(false);
  const [testimonialSaving, setTestimonialSaving] = useState(false);
  const [testimonialError, setTestimonialError] = useState('');
  const [testimonialSuccess, setTestimonialSuccess] = useState('');

  // --- CTA Section ---
  const [cta, setCta] = useState({ cta_title: '', cta_description: '', cta_button1_text: '', cta_button1_link: '', cta_button2_text: '', cta_button2_link: '' });
  const [ctaLoading, setCtaLoading] = useState(true);
  const [ctaEdit, setCtaEdit] = useState(false);
  const [ctaSaving, setCtaSaving] = useState(false);
  const [ctaError, setCtaError] = useState('');
  const [ctaSuccess, setCtaSuccess] = useState('');

  // --- Fetch Data ---
  const fetchProcessData = async () => {
    try {
      console.log('🔄 fetchProcessData called...'); // Debug log
      setProcessLoading(true);
      setStepsLoading(true);
      const res = await axios.get(`${API_BASE}/process`);
      const processData = res.data.data;
      console.log('Fetched process data:', processData); // Debug log
      
      // Ensure steps is always an array
      const stepsData = Array.isArray(processData.steps) ? processData.steps : [];
      console.log('Processed steps data:', stepsData); // Debug log
      console.log('Steps count:', stepsData.length); // Debug log
      
      // Update both states simultaneously to ensure sync
      setProcess({ 
        process_title: processData.process_title || '', 
        process_description: processData.process_description || '',
        process_steps: [...stepsData] // Create new array reference
      });
      setSteps([...stepsData]); // Create new array reference
      console.log('✅ State updated with steps:', stepsData.length); // Debug log
      console.log('✅ Steps state:', stepsData.length, 'items'); // Debug log
      console.log('✅ Process steps state:', stepsData.length, 'items'); // Debug log
      setForceRender(prev => prev + 1); // Force re-render
      setProcessLoading(false);
      setStepsLoading(false);
    } catch (error) {
      console.error('Error fetching process data:', error);
      setProcessLoading(false);
      setStepsLoading(false);
    }
  };

  useEffect(() => {
    setMainLoading(true);
    axios.get(`${API_BASE}/main`).then(res => {
      setMain(res.data.data || { main_title: '', gradient_title: '', description: '' });
      setMainLoading(false);
    });
    setStatsLoading(true);
    axios.get(`${API_BASE}/stats`).then(res => {
      const statsData = res.data.data;
      setStats(Array.isArray(statsData) ? statsData : []);
      setStatsLoading(false);
    });
    setAdditionalLoading(true);
    axios.get(`${API_BASE}/additional`).then(res => {
      setAdditional(res.data.data || { additional_title: '', additional_description: '' });
      const additionalServicesData = res.data.data.additional_services;
      setAdditionalServices(additionalServicesData ? 
        (typeof additionalServicesData === 'string' ? 
          JSON.parse(additionalServicesData) : 
          additionalServicesData) : []);
      setAdditionalLoading(false);
    });
    
    // Use the new fetchProcessData function
    fetchProcessData();
    
    setTestimonialLoading(true);
    axios.get(`${API_BASE}/testimonials`).then(res => {
      setTestimonialSection({ testimonial_title: res.data.data.testimonial_title, testimonial_description: res.data.data.testimonial_description });
      setTestimonials(res.data.data.testimonials || []);
      setTestimonialLoading(false);
    });
    setCtaLoading(true);
    axios.get(`${API_BASE}/cta`).then(res => {
      setCta(res.data.data || {});
      setCtaLoading(false);
    });
  }, []);

  // Monitor stepForm changes
  useEffect(() => {
    console.log('stepForm changed:', stepForm);
  }, [stepForm]);

  // --- Main Section Handlers (already implemented above) ---
  // Handle input change
  const handleMainChange = e => {
    setMain({ ...main, [e.target.name]: e.target.value });
  };

  // Save main section
  const saveMain = async e => {
    e.preventDefault();
    setMainSaving(true);
    setMainError('');
    setMainSuccess('');
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API_BASE}/main`, main, { headers });
      notify.success('Main section updated successfully!', 'Success');
      setMainEdit(false);
    } catch (err) {
      notify.error('Failed to update main section', 'Update Error');
    } finally {
      setMainSaving(false);
    }
  };

  // --- Stats Section Handlers ---
  const handleStatChange = e => setStatForm({ ...statForm, [e.target.name]: e.target.value });
  const handleStatIcon = icon => setStatForm({ ...statForm, icon });
  const startEditStat = stat => { setStatEditId(stat.id); setStatForm({ label: stat.label, value: stat.value, icon: stat.icon }); };
  const cancelEditStat = () => { setStatEditId(null); setStatForm({ label: '', value: '', icon: ICONS[0] }); setStatError(''); setStatSuccess(''); };
  const saveStat = async e => {
    e.preventDefault();
    setStatSaving(true);
    setStatError('');
    setStatSuccess('');
    try {
      if (statEditId) {
        const token = localStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.put(`${API_BASE}/stats/${statEditId}`, statForm, { headers });
        notify.success('Stat updated successfully!', 'Success');
      } else {
        const token = localStorage.getItem('authToken');
        const headers = token ? { Authorization: `Bearer ${token}` } : {};
        await axios.post(`${API_BASE}/stats`, statForm, { headers });
        notify.success('Stat added successfully!', 'Success');
      }
      // Fetch updated stats to ensure we have the latest data including icons
      const res = await axios.get(`${API_BASE}/stats`);
      const statsData = res.data.data;
      setStats(Array.isArray(statsData) ? statsData : []);
      cancelEditStat();
      setStatAddMode(false);
    } catch {
      notify.error('Failed to save stat', 'Save Error');
    } finally {
      setStatSaving(false);
    }
  };
  const deleteStat = async id => {
    if (!window.confirm('Delete this stat?')) return;
    setStatSaving(true);
    setStatError('');
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_BASE}/stats/${id}`, { headers });
      setStats(Array.isArray(stats) ? stats.filter(s => s.id !== id) : []);
      notify.success('Stat deleted successfully!', 'Success');
    } catch {
      notify.error('Failed to delete stat', 'Delete Error');
    } finally {
      setStatSaving(false);
    }
  };
  // Reorder stats
  const moveStat = (idx, dir) => {
    if (!Array.isArray(stats)) return;
    const newStats = [...stats];
    if (dir === 'up' && idx > 0) {
      [newStats[idx - 1], newStats[idx]] = [newStats[idx], newStats[idx - 1]];
    } else if (dir === 'down' && idx < newStats.length - 1) {
      [newStats[idx + 1], newStats[idx]] = [newStats[idx], newStats[idx + 1]];
    }
    // Update order_index in DB
    newStats.forEach((s, i) => { s.order_index = i; });
    setStats([...newStats]);
    // Persist order
    newStats.forEach(async s => {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API_BASE}/stats/${s.id}`, { ...s, order_index: s.order_index }, { headers });
    });
  };

  // Persist the full statistics list (handles added/removed/edited rows)
  const saveStatsSection = async () => {
    setStatSaving(true);
    setStatError('');
    setStatSuccess('');
    try {
      const token = localStorage.getItem('authToken');
      const headers = {
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` }),
      };
      const payload = (Array.isArray(stats) ? stats : []).filter(
        s => (s.label || '').trim() && String(s.value ?? '').trim()
      );
      await axios.put(`${API_BASE}/stats`, { stats: payload }, { headers });
      const res = await axios.get(`${API_BASE}/stats`);
      setStats(Array.isArray(res.data.data) ? res.data.data : []);
      setStatSuccess('Statistics saved successfully!');
      notify.success('Statistics saved successfully!', 'Success');
    } catch (err) {
      setStatError('Failed to save statistics');
      notify.error('Failed to save statistics', 'Save Error');
    } finally {
      setStatSaving(false);
    }
  };

  // --- Additional Services Handlers ---
  const handleAdditionalChange = e => setAdditional({ ...additional, [e.target.name]: e.target.value });
  const saveAdditional = async e => {
    e.preventDefault();
    setAdditionalSaving(true);
    setAdditionalError('');
    setAdditionalSuccess('');
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API_BASE}/additional`, additional, { headers });
      setAdditionalSuccess('Section updated!');
      setAdditionalEdit(false);
    } catch {
      setAdditionalError('Failed to update section');
    } finally {
      setAdditionalSaving(false);
    }
  };

  // --- Our Process Handlers ---
  const handleProcessChange = e => setProcess({ ...process, [e.target.name]: e.target.value });
  const saveProcess = async e => {
    e.preventDefault();
    setProcessSaving(true);
    setProcessError('');
    setProcessSuccess('');
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API_BASE}/process`, process, { headers });
      setProcessSuccess('Section updated!');
      setProcessEdit(false);
    } catch {
      setProcessError('Failed to update section');
    } finally {
      setProcessSaving(false);
    }
  };
  // Steps CRUD
  const handleStepChange = e => setStepForm({ ...stepForm, [e.target.name]: e.target.value });
  const handleStepIcon = icon => {
    console.log('Icon changed to:', icon); // Debug log
    console.log('Current stepForm before update:', stepForm); // Debug log
    setStepForm({ ...stepForm, icon });
    console.log('stepForm will be updated to:', { ...stepForm, icon }); // Debug log
  };
  const startEditStep = step => { 
    console.log('Starting edit for step:', step); // Debug log
    console.log('Step icon value:', step.icon); // Debug log
    setStepEditId(step.id); 
    const formData = { 
      title: step.title, 
      description: step.description, 
      icon: step.icon || ICONS[0], // Ensure icon is never null/undefined
      order_index: step.order_index || 0 // Include order_index
    };
    console.log('Setting form data:', formData); // Debug log
    setStepForm(formData); 
  };
  const cancelEditStep = () => { 
    setStepEditId(null); 
    setStepForm({ title: '', description: '', icon: ICONS[0], order_index: 0 }); 
    setStepError(''); 
    setStepSuccess(''); 
  };
  const saveStep = async e => {
    e.preventDefault();
    setStepSaving(true);
    setStepError('');
    setStepSuccess('');
    try {
      console.log('Saving step with form:', stepForm); // Debug log
      
      // Check authentication
      const token = localStorage.getItem('authToken');
      console.log('Auth token exists:', !!token); // Debug log
      console.log('Auth token:', token ? token.substring(0, 20) + '...' : 'none'); // Debug log
      
      if (!token) {
        setStepError('Authentication required. Please log in again.');
        return;
      }
      
      const headers = { 
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${token}` 
      };
      
      if (stepEditId) {
        console.log('Updating step with headers:', headers); // Debug log
        // Ensure order_index is included for updates
        const updateData = {
          ...stepForm,
          order_index: stepForm.order_index || 0
        };
        console.log('Update data with order_index:', updateData); // Debug log
        const response = await axios.put(`${API_BASE}/process/step/${stepEditId}`, updateData, { headers });
        console.log('Update response:', response.data); // Debug log
        setStepSuccess('Step updated!');
      } else {
        console.log('Adding step with headers:', headers); // Debug log
        const response = await axios.post(`${API_BASE}/process/step`, stepForm, { headers });
        console.log('Add response:', response.data); // Debug log
        setStepSuccess('Step added!');
      }
      
      // Use the centralized fetch function to reload data
      console.log('Reloading process data after save...'); // Debug log
      await fetchProcessData();
      
      // Force a re-render by updating state
      setTimeout(() => {
        console.log('Forcing re-render...'); // Debug log
        fetchProcessData();
      }, 100);
      
      // Clear form and edit state
      cancelEditStep();
      setStepAddMode(false);
    } catch (error) {
      console.error('Error saving step:', error.response?.data || error.message);
      if (error.response?.status === 401) {
        setStepError('Authentication failed. Please log in again.');
      } else {
        setStepError(`Failed to save step: ${error.response?.data?.message || error.message}`);
      }
    } finally {
      setStepSaving(false);
    }
  };
  const deleteStep = async id => {
    if (!window.confirm('Delete this step?')) return;
    setStepSaving(true);
    setStepError('');
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.delete(`${API_BASE}/process/step/${id}`, { headers });
      
      // Use the centralized fetch function to reload data
      console.log('Reloading process data after delete...'); // Debug log
      await fetchProcessData();
      
      // Force a re-render by updating state
      setTimeout(() => {
        console.log('Forcing re-render after delete...'); // Debug log
        fetchProcessData();
      }, 100);
    } catch (error) {
      console.error('Error deleting step:', error.response?.data || error.message);
      setStepError(`Failed to delete step: ${error.response?.data?.message || error.message}`);
    } finally {
      setStepSaving(false);
    }
  };
  // Reorder steps
  const moveStep = async (idx, dir) => {
    const newSteps = [...steps];
    if (dir === 'up' && idx > 0) {
      [newSteps[idx - 1], newSteps[idx]] = [newSteps[idx], newSteps[idx - 1]];
    } else if (dir === 'down' && idx < newSteps.length - 1) {
      [newSteps[idx + 1], newSteps[idx]] = [newSteps[idx], newSteps[idx + 1]];
    }
    newSteps.forEach((s, i) => { s.order_index = i; });
    
    try {
      // Update all steps in database with new order
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      
      await Promise.all(newSteps.map(async s => {
        await axios.put(`${API_BASE}/process/step/${s.id}`, { 
          title: s.title, 
          description: s.description, 
          icon: s.icon, 
          order_index: s.order_index 
        }, { headers });
      }));
      
      // Reload data to ensure consistency
      await fetchProcessData();
    } catch (error) {
      console.error('Error reordering steps:', error);
      setStepError('Failed to reorder steps');
    }
  };

  // --- Testimonials Handlers ---
  const handleTestimonialSectionChange = e => setTestimonialSection({ ...testimonialSection, [e.target.name]: e.target.value });
  const saveTestimonialSection = async e => {
    e.preventDefault();
    setTestimonialSaving(true);
    setTestimonialError('');
    setTestimonialSuccess('');
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API_BASE}/testimonials`, testimonialSection, { headers });
      setTestimonialSuccess('Section updated!');
      setTestimonialEdit(false);
    } catch {
      setTestimonialError('Failed to update section');
    } finally {
      setTestimonialSaving(false);
    }
  };

  // --- CTA Handlers ---
  const handleCtaChange = e => setCta({ ...cta, [e.target.name]: e.target.value });
  const saveCta = async e => {
    e.preventDefault();
    setCtaSaving(true);
    setCtaError('');
    setCtaSuccess('');
    try {
      const token = localStorage.getItem('authToken');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      await axios.put(`${API_BASE}/cta`, cta, { headers });
      setCtaSuccess('Section updated!');
      setCtaEdit(false);
    } catch {
      setCtaError('Failed to update section');
    } finally {
      setCtaSaving(false);
    }
  };

  // --- Save All Sections ---
  const [savingAll, setSavingAll] = useState(false);
  const [saveAllError, setSaveAllError] = useState('');
  const [saveAllSuccess, setSaveAllSuccess] = useState('');

  const saveAllSections = async () => {
    if (!isEditing) return;
    
    setSavingAll(true);
    setSaveAllError('');
    setSaveAllSuccess('');
    
    try {
      const token = localStorage.getItem('authToken');
      const headers = { 
        'Content-Type': 'application/json',
        ...(token && { Authorization: `Bearer ${token}` })
      };
      
      await Promise.all([
        axios.put(`${API_BASE}/main`, main, { headers }),
        axios.put(`${API_BASE}/stats`, { stats }, { headers }),
        axios.put(`${API_BASE}/additional`, { 
          additional_title: additional.additional_title,
          additional_description: additional.additional_description,
          additional_services: additionalServices
        }, { headers }),
        axios.put(`${API_BASE}/process`, { 
          process_title: process.process_title,
          process_description: process.process_description
        }, { headers }),
        axios.put(`${API_BASE}/testimonials`, { 
          testimonial_title: testimonialSection.testimonial_title,
          testimonial_description: testimonialSection.testimonial_description
        }, { headers }),
        axios.put(`${API_BASE}/cta`, cta, { headers })
      ]);
      
      setSaveAllSuccess('All sections saved successfully!');
    } catch (error) {
      console.error('Error saving all sections:', error);
      setSaveAllError('Failed to save all sections');
    } finally {
      setSavingAll(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-lg">
          {/* Header */}
          <div className="border-b border-gray-200 dark:border-gray-700 px-6 py-4">
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white">Services Page Management</h1>
                <p className="text-gray-600 dark:text-gray-400">Manage your services page content</p>
                {saveAllSuccess && (
                  <div className="mt-2 text-sm text-green-600 dark:text-green-400">{saveAllSuccess}</div>
                )}
                {saveAllError && (
                  <div className="mt-2 text-sm text-red-600 dark:text-red-400">{saveAllError}</div>
                )}
              </div>
              <div className="flex items-center gap-4">
                {isEditing ? (
                  <>
                    <button
                      onClick={() => setIsEditing(false)}
                      className="flex items-center gap-2 px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-800 dark:hover:text-gray-200"
                    >
                      <Eye className="w-4 h-4" />
                      View
                    </button>
                    <button
                      onClick={saveAllSections}
                      className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50"
                      disabled={savingAll}
                    >
                      {savingAll ? 'Saving...' : 'Save Changes'}
                      <Save className="w-4 h-4" />
                    </button>
                  </>
                ) : (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                  >
                    <Edit className="w-4 h-4" />
                    Edit Content
                  </button>
                )}
              </div>
            </div>
          </div>
          <div className="flex">
            {/* Sidebar */}
            <div className="w-64 border-r border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 sm:p-6">
              <nav className="space-y-2">
                {SECTIONS.map((section) => {
                  const IconComponent = section.icon;
                  return (
                    <button
                      key={section.id}
                      onClick={() => setActiveSection(section.id)}
                      className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-left transition-colors ${
                        activeSection === section.id
                          ? 'bg-primary-100 dark:bg-primary-900 text-primary-700 dark:text-primary-300'
                          : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700'
                      }`}
                    >
                      <IconComponent className="w-4 h-4" />
                      <span className="text-sm font-medium">{section.label}</span>
                    </button>
                  );
                })}
              </nav>
            </div>
            {/* Main Content */}
            <div className="flex-1 p-8">
              {/* Render main section here, styled like AboutUsManagement */}
              {activeSection === 'main' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Main Section</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Main Title</label>
                    <input
                      type="text"
                      value={main.main_title}
                      onChange={e => setMain({ ...main, main_title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Our"
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Gradient Title</label>
                    <input
                      type="text"
                      value={main.gradient_title}
                      onChange={e => setMain({ ...main, gradient_title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Services"
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                    <textarea
                      value={main.description}
                      onChange={e => setMain({ ...main, description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Comprehensive digital solutions to elevate your business"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              )}
              {activeSection === 'stats' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Statistics</h3>
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Statistics Items</label>
                      {isEditing && (
                        <button
                          onClick={() => {
                            console.log('Adding stat to stats:', stats);
                            setStats([...(Array.isArray(stats) ? stats : []), { label: '', value: '', icon: ICONS[0] }]);
                          }}
                          className="flex items-center gap-2 px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                          <Plus className="w-4 h-4" />
                          Add Stat
                        </button>
                      )}
                    </div>
                    <div className="space-y-4">
                      {Array.isArray(stats) && stats.map((stat, index) => (
                        <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Stat {index + 1}</span>
                            {isEditing && (
                              <button
                                onClick={() => setStats(Array.isArray(stats) ? stats.filter((_, i) => i !== index) : [])}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Label</label>
                              <input
                                type="text"
                                value={stat.label || ''}
                                onChange={e => {
                                  const newStats = Array.isArray(stats) ? [...stats] : [];
                                  newStats[index] = { ...newStats[index], label: e.target.value };
                                  setStats(newStats);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="Projects Completed"
                                disabled={!isEditing}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Value</label>
                              <input
                                type="text"
                                value={stat.value || ''}
                                onChange={e => {
                                  const newStats = Array.isArray(stats) ? [...stats] : [];
                                  newStats[index] = { ...newStats[index], value: e.target.value };
                                  setStats(newStats);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="500+"
                                disabled={!isEditing}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Icon</label>
                              <select
                                value={stat.icon || ''}
                                onChange={e => {
                                  const newStats = Array.isArray(stats) ? [...stats] : [];
                                  newStats[index] = { ...newStats[index], icon: e.target.value };
                                  setStats(newStats);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                disabled={!isEditing}
                              >
                                <option value="">Select an icon</option>
                                {ICONS.map(icon => (
                                  <option key={icon} value={icon}>{icon}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                    {isEditing && (
                      <div className="flex items-center justify-end gap-3 mt-4">
                        {statError && <span className="text-sm text-red-500">{statError}</span>}
                        {statSuccess && <span className="text-sm text-green-600">{statSuccess}</span>}
                        <button
                          onClick={saveStatsSection}
                          disabled={statSaving}
                          className="flex items-center gap-2 px-4 py-2 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-60"
                        >
                          {statSaving ? 'Saving...' : 'Save Statistics'}
                        </button>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeSection === 'additional' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Additional Services</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Section Title</label>
                    <input
                      type="text"
                      value={additional.additional_title}
                      onChange={e => setAdditional({ ...additional, additional_title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Additional Services"
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Section Description</label>
                    <textarea
                      value={additional.additional_description}
                      onChange={e => setAdditional({ ...additional, additional_description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Comprehensive solutions for all your digital needs"
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Additional Services</label>
                      {isEditing && (
                        <button
                          onClick={() => {
                            setAdditionalServices([...additionalServices, { title: '', description: '', icon: '' }]);
                          }}
                          className="flex items-center gap-2 px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                          <Plus className="w-4 h-4" />
                          Add Service
                        </button>
                      )}
                    </div>
                    <div className="space-y-4">
                      {Array.isArray(additionalServices) && additionalServices.map((service, index) => (
                        <div key={index} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                          <div className="flex items-center justify-between mb-3">
                            <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Service {index + 1}</span>
                            {isEditing && (
                              <button
                                onClick={() => setAdditionalServices(additionalServices.filter((_, i) => i !== index))}
                                className="text-red-500 hover:text-red-700"
                              >
                                <Trash2 className="w-4 h-4" />
                              </button>
                            )}
                          </div>
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                              <input
                                type="text"
                                value={service.title || ''}
                                onChange={e => {
                                  const newServices = [...additionalServices];
                                  newServices[index] = { ...newServices[index], title: e.target.value };
                                  setAdditionalServices(newServices);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="Service Title"
                                disabled={!isEditing}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                              <input
                                type="text"
                                value={service.description || ''}
                                onChange={e => {
                                  const newServices = [...additionalServices];
                                  newServices[index] = { ...newServices[index], description: e.target.value };
                                  setAdditionalServices(newServices);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                placeholder="Service Description"
                                disabled={!isEditing}
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Icon</label>
                              <select
                                value={service.icon || ''}
                                onChange={e => {
                                  const newServices = [...additionalServices];
                                  newServices[index] = { ...newServices[index], icon: e.target.value };
                                  setAdditionalServices(newServices);
                                }}
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                disabled={!isEditing}
                              >
                                <option value="">Select an icon</option>
                                {ICONS.map(icon => (
                                  <option key={icon} value={icon}>{icon}</option>
                                ))}
                              </select>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              )}

              {activeSection === 'process' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Our Process</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Section Title</label>
                    <input
                      type="text"
                      value={process.process_title}
                      onChange={e => setProcess({ ...process, process_title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Our Process"
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Section Description</label>
                    <textarea
                      value={process.process_description}
                      onChange={e => setProcess({ ...process, process_description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="How we work to deliver exceptional results"
                      disabled={!isEditing}
                    />
                  </div>
                  
                  {/* Process Steps Management */}
                  <div>
                    <div className="flex items-center justify-between mb-4">
                      <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">Process Steps</label>
                      {isEditing && (
                        <button
                          onClick={() => { 
                            console.log('Add Step clicked, setting stepAddMode to true');
                            setStepAddMode(true); 
                            cancelEditStep(); 
                          }}
                          className="flex items-center gap-2 px-3 py-1 text-sm bg-primary-600 text-white rounded-lg hover:bg-primary-700"
                        >
                          <Plus className="w-4 h-4" />
                          Add Step
                        </button>
                      )}
                    </div>
                    
                    {/* Debug Section - Only show in development */}
                    {false && (
                      <div className="bg-yellow-50 dark:bg-yellow-900/20 p-3 rounded-lg mb-4 text-sm">
                        <strong>Debug Info:</strong>
                        <div>stepAddMode: {stepAddMode ? 'true' : 'false'}</div>
                        <div>Steps count: {steps.length}</div>
                        <div>stepEditId: {stepEditId || 'null'}</div>
                      </div>
                    )}
                    
                    {stepsLoading ? (
                      <div className="text-gray-500">Loading...</div>
                    ) : (
                      <div className="space-y-4">
                        {Array.isArray(steps) && steps.map((step, index) => (
                          <div key={step.id} className="border border-gray-200 dark:border-gray-600 rounded-lg p-4">
                            <div className="flex items-center justify-between mb-3">
                              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">Step {index + 1}</span>
                              {isEditing && (
                                <div className="flex items-center space-x-2">
                                  <button
                                    onClick={() => moveStep(index, 'up')}
                                    disabled={index === 0}
                                    className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                    title="Move Up"
                                  >
                                    <ArrowUp className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => moveStep(index, 'down')}
                                    disabled={index === steps.length - 1}
                                    className="text-gray-500 hover:text-gray-700 disabled:opacity-50"
                                    title="Move Down"
                                  >
                                    <ArrowDown className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => startEditStep(step)}
                                    className="text-blue-500 hover:text-blue-700"
                                    title="Edit Step"
                                  >
                                    <Edit className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => deleteStep(step.id)}
                                    className="text-red-500 hover:text-red-700"
                                    title="Delete Step"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              )}
                            </div>
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                                <input
                                  type="text"
                                  value={step.title || ''}
                                  onChange={e => {
                                    const newSteps = [...steps];
                                    newSteps[index] = { ...newSteps[index], title: e.target.value };
                                    setSteps(newSteps);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  placeholder="Step Title"
                                  disabled={!isEditing}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                                <input
                                  type="text"
                                  value={step.description || ''}
                                  onChange={e => {
                                    const newSteps = [...steps];
                                    newSteps[index] = { ...newSteps[index], description: e.target.value };
                                    setSteps(newSteps);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  placeholder="Step Description"
                                  disabled={!isEditing}
                                />
                              </div>
                              <div>
                                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Icon</label>
                                <select
                                  value={step.icon || ''}
                                  onChange={e => {
                                    const newSteps = [...steps];
                                    newSteps[index] = { ...newSteps[index], icon: e.target.value };
                                    setSteps(newSteps);
                                  }}
                                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                  disabled={!isEditing}
                                >
                                  <option value="">Select an icon</option>
                                  {ICONS.map(icon => (
                                    <option key={icon} value={icon}>{icon}</option>
                                  ))}
                                </select>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                    
                    {/* Add/Edit Form */}
                    {(stepAddMode || stepEditId) && (
                      <div className="border border-gray-200 dark:border-gray-600 rounded-lg p-4 bg-gray-50 dark:bg-gray-800">
                        <h4 className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-3">
                          {stepEditId ? 'Edit Step' : 'Add New Step'}
                        </h4>
                        <form onSubmit={saveStep} className="space-y-4">
                          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Title</label>
                              <input
                                type="text"
                                name="title"
                                value={stepForm.title}
                                onChange={handleStepChange}
                                placeholder="Step Title"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                              <input
                                type="text"
                                name="description"
                                value={stepForm.description}
                                onChange={handleStepChange}
                                placeholder="Step Description"
                                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                                required
                              />
                            </div>
                            <div>
                              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Icon</label>
                              <IconPicker value={stepForm.icon} onChange={handleStepIcon} />
                            </div>
                          </div>
                          <div className="flex space-x-2">
                            <button
                              type="submit"
                              className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
                              disabled={stepSaving}
                            >
                              {stepSaving ? 'Saving...' : 'Save Step'}
                            </button>
                            <button
                              type="button"
                              className="bg-gray-200 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-300 disabled:opacity-50"
                              onClick={cancelEditStep}
                              disabled={stepSaving}
                            >
                              Cancel
                            </button>
                          </div>
                          {stepError && <div className="text-red-600 mt-2 text-sm">{stepError}</div>}
                          {stepSuccess && <div className="text-green-600 mt-2 text-sm">{stepSuccess}</div>}
                        </form>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {activeSection === 'testimonials' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Testimonials</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Section Title</label>
                    <input
                      type="text"
                      value={testimonialSection.testimonial_title}
                      onChange={e => setTestimonialSection({ ...testimonialSection, testimonial_title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="What Our Clients Say"
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Section Description</label>
                    <textarea
                      value={testimonialSection.testimonial_description}
                      onChange={e => setTestimonialSection({ ...testimonialSection, testimonial_description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Real feedback from satisfied clients"
                      disabled={!isEditing}
                    />
                  </div>
                  <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                    <p className="text-sm text-blue-700 dark:text-blue-300">
                      <strong>Note:</strong> Testimonials are managed separately in the Testimonials Management section. 
                      You can only edit the section title and description here.
                    </p>
                  </div>
                </div>
              )}

              {activeSection === 'cta' && (
                <div className="space-y-6">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white">Call to Action</h3>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Title</label>
                    <input
                      type="text"
                      value={cta.cta_title}
                      onChange={e => setCta({ ...cta, cta_title: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Ready to Get Started?"
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Description</label>
                    <textarea
                      value={cta.cta_description}
                      onChange={e => setCta({ ...cta, cta_description: e.target.value })}
                      rows={3}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Let's discuss your project and bring your vision to life"
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Button Text</label>
                    <input
                      type="text"
                      value={cta.cta_button1_text}
                      onChange={e => setCta({ ...cta, cta_button1_text: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="Get Started"
                      disabled={!isEditing}
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">Button Link</label>
                    <input
                      type="text"
                      value={cta.cta_button1_link}
                      onChange={e => setCta({ ...cta, cta_button1_link: e.target.value })}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-transparent bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      placeholder="/contact"
                      disabled={!isEditing}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ServicesPageManagement; 